import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CurrencyPipe } from '../../../../pipes/currency.pipe';

@Component({
  selector: 'app-select-product-choices',
  standalone: true,
  imports: [CommonModule, CurrencyPipe],
  templateUrl: './select-product-choices.component.html',
  styleUrls: ['./select-product-choices.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SelectProductChoicesComponent implements OnInit {

  @Input() choices: any;
  @Input() preSelectedChoices: any[] = [];
  @Output() selectChoice = new EventEmitter<any>();

  selectedOptions: any[] = [];
  @Input() moduloMaiorValorComplemento: boolean = false;
  constructor() {}

  ngOnInit(): void {
    // Initialize selectedOptions with pre-selected choices if provided
    if (this.preSelectedChoices && this.preSelectedChoices.length > 0) {
      this.selectedOptions = [...this.preSelectedChoices];
    }
  }

  getChoiceSubtitle(choice: any): string {
    return choice.max === 1 ? 'Selecione uma opção' : 'Selecione até ' + choice.max + ' opções';
  }

  isOptionDisabled(choice: any): boolean {
    const totalQuantityForChoice = this.selectedOptions
      .filter((opt: any) => opt.choiceGroupId === choice.id)
      .reduce((sum: number, opt: any) => sum + (opt.quantity || 0), 0);
    return totalQuantityForChoice >= choice.max;
  }

  isOptionSelected(option: any): boolean {
    return this.selectedOptions.some((selectedOption: any) =>
      selectedOption.id === option.id && selectedOption.quantity > 0
    );
  }

  getOptionQuantity(option: any): number {
    const selectedOption = this.selectedOptions.find(opt => opt.id === option.id);
    return selectedOption?.quantity || 1;
  }

  // TODO: Implementar lógica para verificar se a quantidade máxima de opções foi selecionada
  isMaxQuantitySelected(): boolean {
    return false;
  }

  getRemainingQuantity(choice: any, optionId: string): number {
    const totalQuantityForChoice = this.selectedOptions
      .filter((opt: any) => opt.choiceGroupId === choice.id)
      .reduce((sum: number, opt: any) => sum + (opt.quantity || 0), 0);

    const currentOptionQuantity = this.selectedOptions
      .find(opt => opt.id === optionId)?.quantity || 0;

    return choice.max - (totalQuantityForChoice - currentOptionQuantity);
  }

  /**
   * Get the maximum price among selected options in a choice group
   * @param choiceId The choice group ID
   * @returns The maximum price or 0 if no options selected
   */
  getMaxSelectedPrice(choiceId: number): number {
    const selectedInGroup = this.selectedOptions.filter(
      (opt: any) => opt.choiceGroupId === choiceId && opt.quantity > 0
    );

    if (selectedInGroup.length === 0) return 0;

    return Math.max(...selectedInGroup.map((opt: any) => opt.originalPrice || opt.price || 0));
  }

  /**
   * Get the display price for an option
   * Applies use_greater_option_price logic when applicable
   * @param choice The choice group
   * @param option The option
   * @returns The price to display
   */
  getDisplayPrice(choice: any, option: any): number {
    const originalPrice = option.price || 0;

    // Check if this choice should use greater option price logic
    if (choice.use_greater_option_price === 1 && choice.max > 1 && this.moduloMaiorValorComplemento) {
      const maxPrice = this.getMaxSelectedPrice(choice.id);

      // Only apply max price to options cheaper than the max selected price
      if (maxPrice > 0 && originalPrice < maxPrice) {
        return maxPrice;
      }
    }

    // Otherwise, return original price
    return originalPrice;
  }

  /**
   * Check if an option's price has been adjusted to a higher price
   * @param choice The choice group
   * @param option The option
   * @returns True if the displayed price is different from the original price
   */
  isPriceAdjusted(choice: any, option: any): boolean {
    if (choice.use_greater_option_price !== 1 || !this.moduloMaiorValorComplemento || choice.max <= 1) {
      return false;
    }

    const displayPrice = this.getDisplayPrice(choice, option);
    const originalPrice = option.price || 0;

    return displayPrice > originalPrice && displayPrice > 0;
  }

  onQuantityChange(choice: any, option: any, value?: number): void {
    const remainingQuantity = this.getRemainingQuantity(choice, option.id);
    const maxAllowed = remainingQuantity;

    const existingOptionIndex = this.selectedOptions.findIndex(
      (opt: any) => opt.id === option.id
    );
    
    const optionData = {
      ...option,
      choiceGroupId: choice.id,
      quantity: Math.min(Number(value), maxAllowed),
      isSingleChoice: choice.max === 1,
      use_greater_option_price: choice.use_greater_option_price && this.moduloMaiorValorComplemento,
      choiceMax: choice.max,
      originalPrice: option.price // Preserve original price
    };
    if (existingOptionIndex >= 0) {
      this.selectedOptions[existingOptionIndex] = optionData;
    } else {
      this.selectedOptions.push(optionData);
    }
    this.selectChoice.emit(optionData);
  }

  /**
   * Handle radio option selection
   * Removes previous selection from same choice group before adding new one
   */
  onRadioSelect(choice: any, option: any): void {
    // Remove previous selection from this choice group
    this.selectedOptions = this.selectedOptions.filter(
      (opt: any) => opt.choiceGroupId !== choice.id
    );

    // Add new selection with choiceGroupId for tracking
    const selection = {
      ...option,
      choiceGroupId: choice.id,
      quantity: 1,
      isSingleChoice: true,
      use_greater_option_price: choice.use_greater_option_price && this.moduloMaiorValorComplemento,
      choiceMax: choice.max,
      originalPrice: option.price // Preserve original price
    };
    this.selectedOptions.push(selection);
    this.selectChoice.emit(selection);
  }

  /**
   * Handle click on option item (entire block)
   * For multiple choice: increment quantity if not at max
   * For single choice: select as radio
   */
  onOptionClick(choice: any, option: any): void {
    if (choice.max > 1) {
      const totalQuantityForChoice = this.selectedOptions
        .filter((opt: any) => opt.choiceGroupId === choice.id)
        .reduce((sum: number, opt: any) => sum + (opt.quantity || 0), 0);

      if (totalQuantityForChoice >= choice.max) {
        return;
      }

      const existingOption = this.selectedOptions.find(
        (opt: any) => opt.id === option.id
      );

      const currentQuantity = existingOption?.quantity || 0;
      const remainingSlots = choice.max - totalQuantityForChoice;
      const newQuantity = Math.min(currentQuantity + 1, currentQuantity + remainingSlots);

      this.onQuantityChange(choice, option, newQuantity);
      return;
    }

    this.onRadioSelect(choice, option);
    return;
  }

  /**
   * Handle increment/decrement button clicks
   * Stops event propagation to prevent triggering onOptionClick
   */
  onQuantityButtonClick(event: Event, choice: any, option: any, newQuantity: number): void {
    event.stopPropagation();
    this.onQuantityChange(choice, option, newQuantity);
  }
}